import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { getAuthHeaders } from '@/hooks/useAuth';

// Weekend campaign landing page — styled to match the Ticket Rewards email templates
// (cream / near-black / yellow, serif-italic display, Courier eyebrows, hairline
// borders, black-on-yellow CTA). Drives PAID Hey Joe Plus signups; the $25 code is
// granted server-side only after the subscription is active.
const CAMPAIGN = 'ticketrewards_launch';

// Palette lifted from the email templates.
const CREAM = '#FFFBF0', INK = '#0A0A0A', SUB = '#6E6A60', YELLOW = '#FFD60A', LINE = '#E8E2D2';
const SERIF = "Georgia, 'Times New Roman', serif";
const MONO = "'Courier New', Courier, monospace";
const SANS = "Helvetica, Arial, sans-serif";

const PERKS: [string, string, boolean][] = [
  ['Reels & skills videos', 'Add up to 10 video reels and skills videos to your talent profile.', false],
  ['Audition Enhancer', 'Polish and sharpen your self-tapes before you send them.', false],
  ['Book a Reader', 'On-demand reader services for your audition tapes.', false],
  ['Marketplace deals', 'Member-only perks and discounts.', true],
  ['Community tools', 'Connect with other actors in the Hey Joe community.', true],
];

const TicketRewardsLanding: React.FC = () => {
  const { isAuthenticated, login } = useAuthContext();
  const { isSubscriber, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  // Evergreen mode: broad-campaign traffic (?c=broad-...) drops the July-6 flash-sale
  // framing and shows the ongoing "$25 every 4 months" member perk instead. Flash-sale
  // traffic (default / any other campaign tag) still sees the July-6 version.
  const [params] = useSearchParams();
  const evergreen = (params.get('c') || '').startsWith('broad');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);

  const startCheckout = async (token?: string) => {
    const res = await fetch('/api/subscription/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : getAuthHeaders()) },
      credentials: 'include',
      body: JSON.stringify({ tier: 'monthly', campaign: CAMPAIGN }),
    });
    const data = await res.json();
    if (res.ok && data.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
    if (data.code === 'ALREADY_SUBSCRIBED') throw new Error("You're already on Hey Joe Plus — your rewards are stacking up automatically.");
    throw new Error(data.error || 'Could not start checkout — please try again.');
  };

  const subscribeNow = async () => {
    setError(null); setLoading(true);
    try { await startCheckout(); } catch (e: any) { setError(e.message); setLoading(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setEmailExists(false);
    if (!firstName || !lastName || !email || password.length < 6) {
      setError('Please fill everything in (password 6+ characters).'); return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName }),
      });
      const data = await res.json();
      if (res.ok && data.token && data.user) { login(data.token, data.user); await startCheckout(data.token); }
      else if (data.errorType === 'email_exists') { setEmailExists(true); setLoading(false); }
      else { setError(data.error || 'Something went wrong — please try again.'); setLoading(false); }
    } catch { setError('Could not reach the server — please try again.'); setLoading(false); }
  };

  const eyebrow: React.CSSProperties = { fontFamily: MONO, fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: SUB };
  const input: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 0, fontSize: 15, border: `1px solid ${LINE}`,
    background: '#fff', color: INK, fontFamily: SANS, boxSizing: 'border-box',
  };
  const ctaBtn: React.CSSProperties = {
    width: '100%', padding: '15px 22px', borderRadius: 0, border: `2px solid ${INK}`, background: INK,
    color: YELLOW, fontSize: 15, fontWeight: 'bold', letterSpacing: 0.3, fontFamily: SANS,
    cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.65 : 1,
  };

  return (
    <main style={{ background: CREAM, minHeight: '100vh', padding: '24px 12px', fontFamily: SANS, color: INK }}>
      <div style={{ maxWidth: 600, margin: '0 auto', background: '#fff', border: `1px solid ${LINE}` }}>

        {/* Black hero */}
        <div style={{ background: INK, padding: '44px 32px 40px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
            <img src="/brand/heyjoe-email-logo.png" alt="hey joe" style={{ height: 36, width: 'auto', display: 'block', filter: 'invert(96%) sepia(8%) saturate(280%) hue-rotate(345deg) brightness(102%)' }} />
            <span style={{ fontFamily: SERIF, fontSize: 22, color: YELLOW }}>&times;</span>
            <img src="/brand/ticket-rewards-knockout.png" alt="Ticket Rewards" style={{ height: 22, width: 'auto', display: 'block' }} />
          </div>
          <p style={{ fontFamily: MONO, fontSize: 16, fontWeight: 'bold', letterSpacing: 1.5, textTransform: 'uppercase', color: YELLOW, margin: '0 0 8px' }}>{evergreen ? '$25 every 4 months you’re a member' : 'Offer ends July 6 · while supplies last'}</p>
          <p style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 'bold', letterSpacing: 1.8, textTransform: 'uppercase', color: CREAM, margin: '0 0 14px' }}>Movies · Concerts · Live Events</p>
          <h1 style={{ margin: 0, fontFamily: SERIF, fontSize: 38, lineHeight: 1.06, fontWeight: 'normal', color: CREAM, letterSpacing: '-1px' }}>
            Get a <em style={{ color: YELLOW, fontStyle: 'italic' }}>$25</em> ticket reward — then a new one every 4 months.
          </h1>
        </div>

        {/* Body */}
        <div style={{ padding: '32px 32px 8px', fontSize: 15, lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 16px' }}>
            Hey Joe has partnered with <strong>Ticket Rewards</strong>. Start <strong>Hey Joe Plus</strong> and your first <strong>$25</strong> code is waiting the moment you subscribe — then a fresh one every four months you stay on Plus. Use it toward movies, concerts &amp; live events. <span style={{ color: SUB }}>(Free profiles don’t include it.)</span>
          </p>

          <p style={{ margin: '20px 0 0' }}>And tickets are just the start — here’s everything else Plus unlocks:</p>

          {/* What else you get */}
          <div style={{ border: `2px solid ${INK}`, margin: '20px 0 28px' }}>
            <div style={{ background: INK, padding: '18px 22px' }}>
              <p style={{ margin: 0, fontFamily: SERIF, fontSize: 21, color: CREAM }}>What else you get on Hey Joe Plus</p>
            </div>
            {PERKS.map(([t, d, soon], i) => (
              <div key={t} style={{ padding: '14px 22px', borderTop: `1px solid ${LINE}`, background: '#fff' }}>
                <p style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 'bold' }}>
                  {t}
                  {soon && <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: SUB, border: `1px solid ${LINE}`, padding: '2px 6px', marginLeft: 8 }}>Coming soon</span>}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: SUB, lineHeight: 1.5 }}>{d}</p>
              </div>
            ))}
          </div>

          {/* Signup / CTA — dashed callout to match the email's offer box */}
          <div style={{ textAlign: 'center', margin: '8px 0 -8px' }}>
            <img src="/brand/tr-char-dancing.png" alt="" style={{ height: 120, width: 'auto', display: 'inline-block' }} />
          </div>
          <div style={{ border: `1px dashed ${INK}`, background: CREAM, padding: 24, margin: '0 0 8px' }}>
            {subLoading ? null : isSubscriber ? (
              <>
                <p style={{ ...eyebrow, color: INK, margin: '0 0 10px' }}>You're a Hey Joe Plus member 🎟️</p>
                <p style={{ margin: '0 0 14px', fontFamily: SERIF, fontSize: 21, lineHeight: 1.3 }}>
                  You're all set — your $25 rewards stack up automatically, a new one every 4 months. Nothing to do here.
                </p>
                <a href="/account" onClick={(e) => { e.preventDefault(); navigate('/account'); }} style={{ ...ctaBtn, display: 'inline-block', width: 'auto', textDecoration: 'none', textAlign: 'center' }}>
                  View my rewards →
                </a>
              </>
            ) : (
              <>
                <p style={{ ...eyebrow, color: INK, fontWeight: 'bold', margin: '0 0 10px' }}>{evergreen ? 'A $25 reward · every 4 months you’re a member' : 'Plus bonus · through July 6 · while supplies last'}</p>
                <p style={{ margin: '0 0 6px', fontFamily: SERIF, fontSize: 21, lineHeight: 1.3 }}>
                  {evergreen
                    ? <>Subscribe and your first <strong>$25</strong> ticket reward is sent the moment you’re in — then a new one every 4 months.</>
                    : <>Subscribe by <strong>July 6</strong> and your first <strong>$25</strong> ticket reward is sent the moment you’re in.</>}
                </p>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: SUB }}>Hey Joe Plus · $9.99/mo · cancel anytime.</p>

                {isAuthenticated ? (
                  <>
                    <button type="button" onClick={subscribeNow} disabled={loading} style={ctaBtn}>
                      {loading ? 'Starting checkout…' : 'Get Hey Joe Plus →'}
                    </button>
                    {error && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#B42318' }}>{error}</p>}
                  </>
                ) : (
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input style={input} placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
                  <input style={input} placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
                </div>
                <input style={input} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input style={input} type="password" placeholder="Password (6+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                {error && <p style={{ margin: 0, fontSize: 13, color: '#B42318' }}>{error}</p>}
                {emailExists && (
                  <p style={{ margin: 0, fontSize: 13, color: SUB }}>
                    You already have a free account.{' '}
                    <a href="/auth" onClick={(e) => { e.preventDefault(); navigate('/auth', { state: { from: '/ticket-rewards' } }); }} style={{ color: INK, fontWeight: 'bold' }}>Sign in</a>
                    {' '}and you'll come right back here to upgrade and claim your code.
                  </p>
                )}
                <button type="submit" disabled={loading} style={ctaBtn}>
                  {loading ? 'Setting up…' : 'Get Hey Joe Plus & my $25 code →'}
                </button>
              </form>
                )}
              </>
            )}
          </div>

          <p style={{ margin: '16px 0 0', fontSize: 11.5, color: SUB, lineHeight: 1.6 }}>
            {evergreen ? 'Codes are limited.' : 'Promo runs through July 6, while supplies last.'} One reward per member. *Minimum order of 2 tickets on Ticket Rewards.{' '}
            <a href="https://www.ticketrewards.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: SUB, textDecoration: 'underline' }}>Terms</a>.
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '24px 32px 28px', background: CREAM, borderTop: `1px solid ${LINE}`, marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img src="/brand/heyjoe-email-logo.png" alt="hey joe" style={{ height: 18, width: 'auto' }} />
            <a href="https://heyjoe.io" style={{ ...eyebrow, color: SUB, textDecoration: 'none' }}>heyjoe.io</a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TicketRewardsLanding;
